import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Register({ setUser }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "end_user", // Always end_user for self-registration
    dealership: "",
    department: ""
  });
  const [loading, setLoading] = useState(false);
  const [dealerships, setDealerships] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchDealershipsAndDepartments();
  }, []);

  const fetchDealershipsAndDepartments = async () => {
    try {
      const [dealershipRes, departmentRes] = await Promise.all([
        axios.get(`${API}/public/dealerships`),
        axios.get(`${API}/public/departments`)
      ]);
      setDealerships(dealershipRes.data);
      setDepartments(departmentRes.data);
    } catch (error) {
      console.error("Failed to fetch dealerships/departments", error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!formData.password.trim()) {
      toast.error("Please enter a password");
      return;
    }
    if (!formData.dealership) {
      toast.error("Please select a dealership");
      return;
    }
    if (!formData.department) {
      toast.error("Please select a department");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      // Don't auto-login anymore - user must verify email first
      toast.success("Registration successful! Please check your email to verify your account.", {
        duration: 6000
      });
      // Clear form
      setFormData({
        email: "",
        password: "",
        name: "",
        role: "end_user",
        dealership: "",
        department: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block bg-white rounded-xl p-6 shadow-[0_35px_96px_0px_rgba(0,0,0,0.55)]">
            <img 
              src="/niello-logo.png" 
              alt="Think Niello" 
              className="h-16 mx-auto"
            />
          </div>
          <p className="text-lg text-slate-60 dark:text-white0 mt-4">Create your account</p>
        </div>
        
        <Card className="shadow-[0_35px_96px_0px_rgba(0,0,0,0.55)] border-0">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">Register</CardTitle>
            <CardDescription>Fill in your details to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => updateField("password", e.target.value)}
                  required
                  data-testid="register-password-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dealership">Dealership *</Label>
                  <Select value={formData.dealership} onValueChange={(value) => updateField("dealership", value)} required>
                    <SelectTrigger data-testid="register-dealership-select">
                      <SelectValue placeholder="Select dealership" />
                    </SelectTrigger>
                    <SelectContent>
                      {dealerships.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Select value={formData.department} onValueChange={(value) => updateField("department", value)} required>
                    <SelectTrigger data-testid="register-department-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-slate-60 dark:text-white0">Already have an account?</span>{" "}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}